import React, { useState, useEffect, useCallback } from "react";
import { Form, message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getAllPartners,
  getAllProducts,
  getAllCategories,
  getCashById,
  createCashWithFile,
  updateCashWithFile,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import CashFormLayout from "./CashFormLayout";
import {
  calculateTotals,
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  prepareFormData,
} from "../../../utils/invoiceUtils";
import createInvoiceItemColumns from "../Utils/InvoiceItemColumns";

const CreateCash = ({ selectedBrand, cashId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const [cashItems, setCashItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(selectedBrand);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_taxAmount: 0,
    calculated_discountAmount: 0,
    calculated_total: 0,
    match: true,
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // Data fetching functions
  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetchBrandsBySelectedClients(selectedClients);
      const brandsArray = response.data || [];
      setBrands(brandsArray);
      setFilteredBrands(brandsArray);
    } catch (error) {
      console.error("Error fetching brands:", error);
      message.error("Failed to fetch brands");
    }
  }, [selectedClients]);

  const fetchPartners = useCallback(async () => {
    try {
      // Add brand filter if a brand is selected
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      const response = await getAllPartners(filters);
      setPartners(response.data);
    } catch (error) {
      console.error("Error fetching partners:", error.response || error);
      message.error("Failed to fetch partners");
    }
  }, [currentBrand, setPartners]);

  const fetchProducts = useCallback(
    async (vendorId = null) => {
      try {
        // Add brand and vendor filters if selected
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllProducts(filters);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error.response || error);
        message.error("Failed to fetch products");
      }
    },
    [currentBrand, setProducts]
  );

  const fetchCategories = useCallback(
    async (vendorId = null) => {
      try {
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllCategories(filters);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error.response || error);
        message.error("Failed to fetch categories");
      }
    },
    [currentBrand, setCategories]
  );

  const handleBrandChange = useCallback(
    (brandId) => {
      const brand = brands.find((b) => b.id === brandId);
      if (brand) {
        setCurrentBrand(brand);
        // Reset partner and items when brand changes
        form.setFieldsValue({ partner: undefined });
        setCashItems([]);
      }
    },
    [brands, form]
  );

  const handlePartnerChange = (value) => {
    const selectedPartner = partners.find((p) => p.id === value);
    if (selectedPartner) {
      // Filter brands based on partner
      if (selectedPartner.brands && selectedPartner.brands.length > 0) {
        const partnerBrandIds = selectedPartner.brands;
        const filtered = brands.filter((b) => partnerBrandIds.includes(b.id));
        setFilteredBrands(filtered);
      } else if (selectedPartner.brand) {
        // Legacy single brand
        const filtered = brands.filter((b) => b.id === selectedPartner.brand);
        setFilteredBrands(filtered);
      } else {
        setFilteredBrands(brands);
      }

      // If the selected partner is a vendor, fetch products and categories for this vendor
      if (
        selectedPartner.partner_type === "vendor" ||
        selectedPartner.partner_type === "both"
      ) {
        fetchProducts(selectedPartner.id);
        fetchCategories(selectedPartner.id);
      } else {
        // If not a vendor, fetch all products and categories for the selected brand
        fetchProducts();
        fetchCategories();
      }
    }
  };

  // Fetch cash data for editing
  const fetchCashData = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const response = await getCashById(id);
        const cashData = response.data;
        console.log("Fetched cash data:", cashData);

        // Format the data for the form
        const formattedItems = cashData.items.map((item) => ({
          key: item.id,
          productId: item.product,
          categoryId: item.category,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          taxRate: item.tax_rate,
          taxAmount: item.tax_amount,
          discountAmount: item.discount_amount || 0,
          subtotal: item.subtotal,
          total_amount: item.total,
        }));

        // Set form values first so calculateTotals can access them
        form.setFieldsValue({
          partner: cashData.partner,
          brand: cashData.brand,
          issueDate: dayjs(cashData.issue_date),
          cashType: cashData.cash_type,
          reference: cashData.reference,
          currency: cashData.currency,
          notes: cashData.notes,
          subtotal: cashData.subtotal || 0,
          discount_amount: cashData.discount_amount || 0,
          tax_rate: cashData.tax_rate || 0,
          total_amount: cashData.total_amount || 0,
          // Handle file URL if present
          fileUrl: cashData.file || null,
        });

        setCashItems(formattedItems);

        // Calculate values using the same utility as when creating cash
        const calculatedValues = calculateTotals(formattedItems, form);
        setCalculatedValues(calculatedValues);
      } catch (error) {
        console.error("Error fetching cash data:", error);
        message.error("Failed to fetch cash data");
      } finally {
        setLoading(false);
      }
    },
    [form, setCashItems, setCalculatedValues, setLoading]
  );

  // Cash items management
  const addItem = () => {
    setCashItems([
      ...cashItems,
      {
        key: Date.now(),
        productId: null,
        categoryId: null,
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        taxAmount: 0,
        discountAmount: 0,
        subtotal: 0,
        total: 0,
      },
    ]);
  };

  const removeItem = (key) => {
    const updatedItems = cashItems.filter((item) => item.key !== key);
    setCashItems(updatedItems);
    calculateCashTotals(updatedItems);
  };

  const handleProductChange = (value, key) => {
    const updatedItems = handleProductChangeUtil(
      value,
      key,
      products,
      cashItems
    );
    if (updatedItems !== cashItems) {
      setCashItems(updatedItems);
      calculateCashTotals(updatedItems);
    }
  };

  const handleItemChange = (key, field, value) => {
    const updatedItems = handleItemChangeUtil(key, field, value, cashItems);
    setCashItems(updatedItems);
    calculateCashTotals(updatedItems);
  };

  const handleCashError = (error, isEditMode) => {
    const action = isEditMode ? "update" : "create";

    // 1. Top‐level detail error
    if (error.response?.data?.detail) {
      message.error(`Failed to ${action} cash: ${error.response.data.detail}`);
      return;
    }

    // 2. Field‐level validation errors
    const errs = error.response?.data;
    if (errs && typeof errs === "object") {
      Object.entries(errs).forEach(([field, messages]) => {
        messages.forEach((msg) => {
          message.error(`${field}: ${msg}`);
        });
      });
      return;
    }

    // 3. Fallback
    message.error(`Failed to ${action} cash: ${error.message}`);
  };

  const calculateCashTotals = (items) => {
    const calculatedValues = calculateTotals(items, form);
    setCalculatedValues(calculatedValues);
  };

  const getCurrentCurrency = () => form.getFieldValue("currency") || "$";

  const onFinish = async (values) => {
    if (!currentBrand) {
      message.error("Please select a brand before saving the cash payment");
      return;
    }

    // Check if total amount matches calculated total
    const formTotalAmount = parseFloat(form.getFieldValue("total_amount") || 0);
    const calculatedTotal = calculatedValues.calculated_total;

    // If totals don't match, show confirmation dialog instead of preventing submission
    if (Math.abs(formTotalAmount - calculatedTotal) > 0.01) {
      const { Modal } = await import("antd");

      Modal.confirm({
        title: "Total Amount Mismatch",
        content: `The total amount (${formTotalAmount}) doesn't match the calculated total (${calculatedTotal}). Do you want to proceed anyway?`,
        okText: "Yes, proceed",
        cancelText: "No, I'll fix it",
        onOk: async () => {
          // Continue with submission if user confirms
          await submitCash(values);
        },
      });
      return;
    }

    // If totals match, proceed with submission
    await submitCash(values);
  };

  // Helper function to submit the cash payment
  const submitCash = async (values) => {
    try {
      setLoading(true);

      // Format the data for submission
      const cashData = {
        ...values,
        // Map camelCase field names to snake_case for backend
        cash_type: values.cashType,
        issue_date: values.issueDate
          ? values.issueDate.format("YYYY-MM-DD")
          : null,
        brand: currentBrand.id,
        items: cashItems.map((item) => ({
          product: item.productId,
          category: item.categoryId,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          tax_rate: item.taxRate,
          tax_amount: item.taxAmount,
          discount_amount: item.discountAmount,
          subtotal: item.subtotal,
          total:
            item.total || item.subtotal + item.taxAmount - item.discountAmount,
        })),
      };

      // Remove camelCase fields that have been converted to snake_case
      delete cashData.cashType;
      delete cashData.issueDate;

      console.log("Submitting cash with data:", cashData);

      // Prepare form data for submission (handles file uploads)
      const formData = prepareFormData(cashData, values);

      if (isEditMode) {
        await updateCashWithFile(cashId, formData);
        message.success("Cash payment updated successfully");
      } else {
        await createCashWithFile(formData);
        message.success("Cash payment created successfully");
      }

      navigate("/accounting/cash");
    } catch (error) {
      handleCashError(error, isEditMode);
    } finally {
      setLoading(false);
    }
  };

  // Initialize form with default values
  useEffect(() => {
    if (form && !isEditMode) {
      form.setFieldsValue({
        currency: "$",
        cashType: "cash", // Frontend uses camelCase
        brand: currentBrand?.id || undefined,
        issueDate: dayjs(), // Frontend uses camelCase
        subtotal: 0,
        discount_amount: 0,
        tax_rate: 0,
        total_amount: 0,
      });
    }
  }, [form, currentBrand, isEditMode]);

  // Handle form value changes
  const onValuesChange = (changedValues) => {
    // If any of the totals fields changed, recalculate
    if (
      changedValues.subtotal !== undefined ||
      changedValues.discount_amount !== undefined ||
      changedValues.tax_rate !== undefined
    ) {
      calculateCashTotals(cashItems);
    }
  };

  // Initialize currentBrand from selectedBrand prop
  useEffect(() => {
    if (selectedBrand) {
      setCurrentBrand(selectedBrand);
    }
  }, [selectedBrand]);

  // Fetch brands on mount
  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Fetch data when brand or cashId changes
  useEffect(() => {
    if (currentBrand) {
      fetchPartners();

      // If cashId is provided, fetch the cash data
      if (cashId || id) {
        setIsEditMode(true);
        fetchCashData(cashId || id);
      }
    } else {
      // Clear all data when no brand is selected
      setPartners([]);
      setProducts([]);
      setCategories([]);
    }
  }, [currentBrand, cashId, id, fetchPartners, fetchCashData]);

  // Create columns for the items table
  const columns = createInvoiceItemColumns(
    products || [], // products
    categories || [], // categories
    [], // accounts placeholder
    [], // offers placeholder
    handleProductChange,
    handleItemChange,
    getCurrentCurrency,
    setCashItems,
    calculateCashTotals,
    removeItem,
    getCurrentCurrency(),
    [], // mailingOffers placeholder
    [], // envelopes placeholder
    cashItems, // items for dynamic column visibility
    [], // lists placeholder
    [], // segments placeholder
    () => { /* no-op: fetchSegments not needed for cash */ }, // fetchSegments placeholder
    navigate
  );

  return (
    <CashFormLayout
      form={form}
      isEditMode={isEditMode}
      loading={loading}
      invoiceItems={cashItems}
      partners={partners}
      categories={categories}
      columns={columns}
      calculatedValues={calculatedValues}
      onFinish={onFinish}
      handlePartnerChange={handlePartnerChange}
      addItem={addItem}
      navigate={navigate}
      selectedBrand={currentBrand}
      brands={filteredBrands}
      allBrands={brands}
      onBrandChange={handleBrandChange}
      onValuesChange={onValuesChange}
      formTitle={isEditMode ? "Edit Cash Payment" : "Create Cash Payment"}
      formType="cash"
    />
  );
};

export default CreateCash;
